import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from './api';
import { useUser } from './user/UserContext';
import CreditUsage from './analytics/CreditUsage';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { BarChart2, Upload, ArrowRight, Bot, Coffee, UserRoundCheck, Link2, Settings, ExternalLink, Lightbulb, Globe, RefreshCw, BookOpen } from 'lucide-react';
// import WeeklyLeads from './analytics/WeeklyLeads';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import DashboardAnalytics from './analytics/DashboardAnalytics';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

interface SessionResponse {
  items: Array<{
    status: string;
    [key: string]: any;
  }>;
  total: number;
  page: number;
  per_page: number;
  pages: number;
  total_sessions: number;
}

interface WebsiteWithoutAutotrain {
  id: string;
  url: string;
  title: string | null;
}

interface RecommendationsResponse {
  crm_connected: boolean;
  websites_without_autotrain: WebsiteWithoutAutotrain[];
}

const Dashboard = () => {
    const navigate = useNavigate();
    const { get, put } = useApi();
    const {
        user,
        organization,
        organizationOnboarding,
        refreshUser,
    } = useUser();
    const [agents, setAgents] = useState<[] | undefined>(undefined);
    const [activeSessions, setActiveSessions] = useState<SessionResponse | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [recommendations, setRecommendations] = useState<RecommendationsResponse | null>(null);
    
    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);

    useEffect(() => {
        const fetchData = async () => {
            await Promise.all([
                getAgents(),
                getActiveSessions(),
                getRecommendations()
            ]);
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (
            agents?.length === 0 &&
            user?.walkthrough_complete &&
            organizationOnboarding !== null &&
            !organizationOnboarding.exists
        ) {
            openModal();
        }
    }, [agents, user, organizationOnboarding]);


    const getAgents = async () => {
        try {
            const response = await get('/agents/');
            setAgents(response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching agents:', error);
            setError('Failed to load agents. Please try again later.');
        }
    };

    const getActiveSessions = async () =>{
        try {
            const response = await get('/sessions/?is_active=true');
            setActiveSessions(response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch active sessions');
            setError('Could not fetch active sessions');
        }
    }

    const getRecommendations = async () => {
        try {
            const response = await get('/recommendations');
            setRecommendations(response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch recommendations:', error);
        }
    }

    useEffect(() => {
        const updateInvitedUser = async () => {
            if (user && user.was_invited && !user.walkthrough_complete) {
                try {
                    await put(`/users/${user.id}/walkthrough-complete/`, {});
                    refreshUser();
                } catch (error) {
                    console.error('Error updating walkthrough status:', error);
                }
            }
        };

        updateInvitedUser();
    }, [refreshUser, user]);

    useEffect(() => {
        const skipWalkthroughForOrgWithAgents = async () => {
            if (
                user &&
                organization &&
                agents !== undefined &&
                agents.length > 0 &&
                user.walkthrough_complete !== true
            ) {
                try {
                    await put(`/users/${user.id}/walkthrough-complete/`, {});
                    refreshUser();
                } catch (error) {
                    console.error('Error marking walkthrough complete for org with existing agents:', error);
                }
            }
        };
        skipWalkthroughForOrgWithAgents();
    }, [user, organization, agents, refreshUser]);

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    const activeSessionCount = activeSessions?.total_sessions ?? 0;
    const firstName = (user as any)?.first_name as string | undefined;

    return (
        <div className="max-h-[calc(100vh-1px)] grow overflow-y-auto px-6 py-8 scrollbar-thin lg:px-8">
            <div className="mx-auto w-full max-w-7xl space-y-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-2xl font-semibold tracking-tight">
                            {getGreeting()}{firstName ? `, ${firstName}` : ''}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Here's what's happening with your agents today.
                        </p>
                    </div>
                    {activeSessionCount > 0 && (
                        <button
                            type="button"
                            onClick={() => navigate('/sessions?is_active=true')}
                            className="group inline-flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/40"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            <span>
                                <span className="font-semibold tabular-nums">{activeSessionCount}</span>
                                <span className="ml-1 text-muted-foreground">
                                    active {activeSessionCount === 1 ? 'session' : 'sessions'}
                                </span>
                            </span>
                            <ArrowRight className="h-3.5 w-3.5 opacity-40 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
                        </button>
                    )}
                </div>

                <CreditUsage />

                <Tabs defaultValue={agents && agents.length > 0 ? "overview" : "get-started"} className="space-y-4">
                    <TabsList className="h-9">
                        <TabsTrigger value="overview"><BarChart2 className='mr-1.5 h-3.5 w-3.5' /> Overview</TabsTrigger>
                        <TabsTrigger value="get-started"><Coffee className='mr-1.5 h-3.5 w-3.5' /> Quickstart</TabsTrigger>
                    </TabsList>

                    <TabsContent value="get-started" className="space-y-6">
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                            <div className="space-y-3">
                                <div>
                                    <h2 className="text-sm font-semibold">Jump back in</h2>
                                    <p className="mt-1 text-xs text-muted-foreground">Open the areas you use most.</p>
                                </div>
                                <div className="overflow-hidden rounded-lg border bg-card divide-y">
                                    {[
                                        {
                                            icon: BarChart2,
                                            title: "View Analytics",
                                            description: "Check your usage and performance metrics",
                                            path: "/analytics",
                                            shortcut: "⌘I",
                                        },
                                        {
                                            icon: Bot,
                                            title: "Manage Agents",
                                            description: "View and configure your existing agents",
                                            path: "/agents",
                                            shortcut: "⌘U",
                                        },
                                        {
                                            icon: Upload,
                                            title: "Update Knowledge Base",
                                            description: "Add documentation to train your agents",
                                            path: "/knowledge",
                                            shortcut: "⌘K",
                                        },
                                        {
                                            icon: UserRoundCheck,
                                            title: "View Contacts",
                                            description: "View your contacts and conversations",
                                            path: "/contacts",
                                            shortcut: "⌘ .",
                                        }
                                    ].map(({ icon: Icon, title, description, path, shortcut }) => (
                                        <button
                                            key={title}
                                            type="button"
                                            onClick={() => navigate(path)}
                                            className="group flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-muted/40"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium">{title}</span>
                                                    <span className="text-xs text-muted-foreground">{description}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <kbd className="hidden sm:inline-flex h-5 items-center rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                                                    {shortcut}
                                                </kbd>
                                                <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 -translate-x-1 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <h2 className="flex items-center gap-1.5 text-sm font-semibold">
                                        <Lightbulb className="h-3.5 w-3.5 text-muted-foreground" />
                                        Recommendations
                                    </h2>
                                    <p className="mt-1 text-xs text-muted-foreground">Suggested improvements for your workspace.</p>
                                </div>
                                <div className="grid gap-3">
                                    {recommendations && !recommendations.crm_connected && (
                                        <button
                                            type="button"
                                            onClick={() => navigate('/integrations')}
                                            className="group flex w-full items-center justify-between rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted/40"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-semibold">Connect your CRM</span>
                                                    <span className="text-xs text-muted-foreground">Sync contacts to HubSpot or Salesforce</span>
                                                </div>
                                            </div>
                                            <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 -translate-x-1 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                                        </button>
                                    )}

                                    {recommendations && recommendations.websites_without_autotrain.length > 0 && (
                                        <div className="overflow-hidden rounded-lg border bg-card">
                                            <div className="flex items-center gap-3 p-4 border-b">
                                                <RefreshCw className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                <div>
                                                    <h3 className="text-sm font-semibold">Enable auto‑train</h3>
                                                    <p className="text-xs text-muted-foreground">
                                                        Keep agents up‑to‑date with automatic retraining
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="divide-y">
                                                {recommendations.websites_without_autotrain.slice(0, 5).map((website) => (
                                                    <div
                                                        key={website.id}
                                                        className="flex items-center justify-between py-2.5 px-4"
                                                    >
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                                                            <span className="text-sm truncate">
                                                                {website.title || website.url}
                                                            </span>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => navigate(`/websites/${website.id}`)}
                                                            className="shrink-0 ml-2 h-8"
                                                        >
                                                            <Settings className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                            {recommendations.websites_without_autotrain.length > 5 && (
                                                <div className="p-3 border-t bg-muted/30">
                                                    <p className="text-xs text-muted-foreground text-center">
                                                        +{recommendations.websites_without_autotrain.length - 5} more websites
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {recommendations && recommendations.crm_connected && recommendations.websites_without_autotrain.length === 0 && (
                                        <div className="flex items-center gap-3 rounded-lg border bg-card p-4">
                                                <svg className="h-4 w-4 shrink-0 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            <div>
                                                <h3 className="text-sm font-semibold">You're all set!</h3>
                                                <p className="text-xs text-muted-foreground">
                                                    No recommendations at this time.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {!recommendations && (
                                        <div className="rounded-lg border bg-card p-4">
                                            <div className="animate-pulse flex items-center gap-3">
                                                <div className="h-10 w-10 rounded-lg bg-muted" />
                                                <div className="flex-1 space-y-2">
                                                    <div className="h-3 bg-muted rounded w-1/2" />
                                                    <div className="h-3 bg-muted rounded w-3/4" />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => window.open('https://docs.aimdoc.ai', '_blank')}
                            className="group flex w-full items-center justify-between rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted/40"
                        >
                            <div className="flex items-center gap-3">
                                <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium">Documentation & guides</span>
                                    <span className="text-xs text-muted-foreground">Explore guides, API references, and tutorials</span>
                                </div>
                            </div>
                            <ExternalLink className="h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </button>
                    </TabsContent>
                    <TabsContent value="overview">
                        <DashboardAnalytics />
                    </TabsContent>
                </Tabs>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
            </div>

            {/* Walkthrough Modal */}
            {/* <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={closeModal}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        Setup Assistance
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            It looks like you haven't created any agents yet. Would you like to create your first agent?
                                        </p>
                                    </div>
                                    <div className="mt-4 flex justify-end gap-4">
                                        <Button variant="outline" onClick={closeModal}>
                                            Maybe Later
                                        </Button>
                                        <Button variant="default" onClick={() => { closeModal(); navigate('/agents/new'); }}>
                                            Create Agent
                                        </Button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition> */}
        </div>
    );
};

export default Dashboard;
